import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ContentPlayer from "arena/components/ContentPlayer";
import ContentBody from "arena/components/ContentBody";
import NextPlaylistContentButton from "arena/components/NextPlaylistContentButton";
import ErrorScreen from "core/components/ErrorScreen";
import { ContentQuery } from "arena/queries";
import { useQuery, useSubscription } from "@apollo/client";
import { ContentViewersOnlineChangedSubscription } from "core/subscriptions";
import analytics from "core/utils/analytics";
import { createEvent } from "core/utils/tracker";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import NoMatchScreen from "arena/components/NoMatchScreen";
import ContentHelmet from "arena/components/ContentHelmet";
import { useArena } from "core/components/ArenaProvider";
import Box from "@mui/material/Box";
import { CloseRouteDialogButton } from "arena/components/RouteDialog";
import useIsMobile from "arena/hooks/useIsMobile";
import RelatedContent from "arena/components/RelatedContent";
import ContentLayout from "arena/components/ContentLayout";
import RelatedContentBannerSlot from "arena/components/RelatedContentBannerSlot";
import ContentComments from "arena/components/ContentComments";
import { useTranslation } from "react-i18next";

const VIDEO = "VIDEO";
const VIDEO_ON_SITE = "VIDEO_ON_SITE";

export default function Video({ params: { contentId }, suspended }) {
  const { t } = useTranslation("arena");
  const isMobile = useIsMobile();
  const arena = useArena();
  const location = useLocation();
  // const { contentId } = useParams();
  const { collectionId, productId, start, finish } = useQueryParams();

  // Текущая секунда плеера для кнопки «Поделиться» в ContentControls (вне
  // плеера): тот же приём, что в PlayerFooter для эмбеда, но через ref,
  // обновляемый по onTimeUpdate, чтобы не ре-рендерить страницу на каждый
  // тик видео.
  const currentTimeRef = useRef(0);
  const getCurrentTime = () => currentTimeRef.current;
  const onTimeUpdate = (videoState) => {
    currentTimeRef.current = videoState?.currentTime;
  };

  // Новое видео: секунда предыдущего не должна попасть в «Начать с» для
  // этого ролика до первого onTimeUpdate.
  useEffect(() => {
    currentTimeRef.current = 0;
  }, [contentId]);

  const onCompleted = (data) => {
    if (data.content) {
      analytics.send({
        hitType: "pageview",
        page: location.pathname,
        title: data.content.title,
      });

      createEvent("page", "load");
    }
  };

  const { data, previousData, loading, error, refetch } = useQuery(
    ContentQuery,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        contentId,
      },
      onCompleted,
    }
  );

  useSubscription(ContentViewersOnlineChangedSubscription, {
    skip: !process.browser || !arena.showVideoViewersCount,
    variables: {
      id: contentId,
    },
  });

  const content = data?.content || previousData?.content;

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (!content && !loading) {
    return <NoMatchScreen />;
  }

  if (content && content.type !== VIDEO) {
    return (
      <Navigate
        to={content.siteUrl.replace("video", content.type.toLowerCase())}
      />
    );
  }

  const showNextContentButton = content?.type === VIDEO;
  // Boolean(productId) || Boolean(collectionId) || ;

  const relatedTabEnabled = !arena?.hideRelatedVideoOnVideo || !!collectionId;
  const showSidebarBanner =
    !isMobile &&
    content?.isAdvEnabled &&
    relatedTabEnabled &&
    content?.rightholder?.id;

  const sidebarHeader = showSidebarBanner ? (
    <RelatedContentBannerSlot
      contentId={contentId}
      rightholderId={content.rightholder.id}
      place={VIDEO}
      variant="sidebar"
    />
  ) : null;

  const tabsSchema = {
    RELATED_VIDEO: {
      label: t("video.tabsSchema.relatedVideo"), //Related Video",
      component: (
        <RelatedContent
          type="VIDEO"
          limit={6}
          selectedContentId={contentId}
          relatedContentId={contentId}
          productId={productId}
          collectionId={collectionId}
          contentId={contentId}
          rightholderId={content?.rightholder?.id}
          place={VIDEO}
          isAdvEnabled={!isMobile && content?.isAdvEnabled}
        />
      ),
      enabled: relatedTabEnabled,
    },
    COMMENTS: {
      label: t("video.tabsSchema.comments"), //"Comments",
      component: <ContentComments contentId={contentId} useParentScroll />,
      enabled: content?.isCommentsEnabled,
    },
  };

  return (
    <Box data-testid="Video">
      <ContentHelmet content={content} arena={arena} />

      <ContentLayout tabsSchema={tabsSchema} sidebarHeader={sidebarHeader}>
        <ContentPlayer
          suspended={suspended}
          sponsorPlayerLogoPlace={VIDEO_ON_SITE}
          sponsorPlayerButtonPlace={VIDEO_ON_SITE}
          sponsorVideoPlace={VIDEO_ON_SITE}
          sponsorInStreamBannerPlace={VIDEO_ON_SITE}
          sponsorCornerBannerPlace={VIDEO_ON_SITE}
          hideTitle
          autoPlay
          content={content}
          loading={loading}
          start={start}
          finish={finish}
          refetch={refetch}
          onTimeUpdate={onTimeUpdate}
          nextPlaylistContentButton={({
            videoState: { duration, currentTime, isEnded },
            visible,
          }) => {
            if (!duration) return null;
            if (!currentTime && currentTime !== 0) return null;
            if (!showNextContentButton) return null;

            const timeLeft = duration - currentTime;

            return (
              <NextPlaylistContentButton
                visible={visible}
                timeLeft={timeLeft}
                contentId={contentId}
                productId={productId}
                collectionId={collectionId}
                isEnded={isEnded}
                type="VIDEO"
              />
            );
          }}
          closeRouteDialogButton={<CloseRouteDialogButton />}
        />
        <ContentBody
          content={content}
          loading={loading}
          getCurrentTime={getCurrentTime}
        />

        {content?.isCommentsEnabled && isMobile && (
          <ContentComments contentId={contentId} isMobile />
        )}
      </ContentLayout>

      {isMobile && (!arena?.hideRelatedVideoOnVideo || !!collectionId) && (
        <RelatedContent
          type="VIDEO"
          limit={6}
          selectedContentId={contentId}
          relatedContentId={contentId}
          productId={productId}
          collectionId={collectionId}
          isMobile
          title={t("video.tabsSchema.relatedVideo")}
          contentId={contentId}
          rightholderId={content?.rightholder?.id}
          place={VIDEO}
          isAdvEnabled={content?.isAdvEnabled}
        />
      )}
    </Box>
  );
}

Video.propTypes = {
  suspended: PropTypes.bool,
  params: PropTypes.shape({
    contentId: PropTypes.string.isRequired,
  }),
};

function useQueryParams() {
  const [searchParams] = useSearchParams();

  const productId = searchParams.get("productId");
  const collectionId = searchParams.get("collectionId");

  const start = searchParams.has("start")
    ? parseInt(searchParams.get("start"), 10)
    : null;

  const finish = searchParams.has("finish")
    ? parseInt(searchParams.get("finish"), 10)
    : null;

  return { collectionId, productId, start, finish };
}
